import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';

import { Subsidiary } from '@/types';

interface SubsidiaryContextType {
  selectedSubsidiary: Subsidiary | null;
  subsidiaries: Subsidiary[];
  setSelectedSubsidiary: (subsidiary: Subsidiary | null) => void;
  isLoading: boolean;
  error: Error | null;
}

const SubsidiaryContext = createContext<SubsidiaryContextType | undefined>(undefined);

const SUBSIDIARY_STORAGE_KEY = 'selected_subsidiary';

// Sentinel object representing the "All Subsidiaries" choice. Both subsidiary
// selectors must use this so the choice can be persisted and restored — the
// API never returns a subsidiary with id 0.
export const ALL_SUBSIDIARIES_SENTINEL: Subsidiary = {
  id: 0,
  name: 'All Subsidiaries',
  code: null,
  active: true,
};

/** D1 (UX review): where a first visit should open; null while unknown or if the call fails (then the old
 *  defaults apply). Shared by the subsidiary and period contexts through one cached query. */
export const useDefaultContext = () => useQuery({
  queryKey: ['default-context'],
  queryFn: async () => {
    try {
      return (await apiService.getDefaultContext?.()) ?? null;
    } catch {
      return null;
    }
  },
  staleTime: Infinity,
});

export const SubsidiaryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedSubsidiary, setSelectedSubsidiaryState] = useState<Subsidiary | null>(null);

  // Fetch subsidiaries from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => {
      return await apiService.getSubsidiaries();
    },
  });

  const subsidiaries = data || [];
  const { data: defaults, isLoading: defaultsLoading } = useDefaultContext();

  // Restore the previously selected subsidiary from localStorage once subsidiaries
  // have loaded. We only fall through to "first subsidiary" on a true first visit
  // (no key in storage); a previously-chosen "All Subsidiaries" is preserved.
  useEffect(() => {
    if (subsidiaries.length === 0) return;
    const firstVisit = !localStorage.getItem(SUBSIDIARY_STORAGE_KEY);
    if (firstVisit && defaultsLoading) return;          // D1: wait for the user's default before choosing

    const storedSubsidiary = localStorage.getItem(SUBSIDIARY_STORAGE_KEY);
    if (storedSubsidiary) {
      try {
        const parsed = JSON.parse(storedSubsidiary);
        if (parsed?.id === 0) {
          if (selectedSubsidiary?.id !== 0) {
            setSelectedSubsidiaryState(ALL_SUBSIDIARIES_SENTINEL);
          }
          return;
        }
        const exists = subsidiaries.find(s => s.id === parsed.id);
        if (exists) {
          setSelectedSubsidiaryState(exists);
          return;
        }
      } catch (e) {
        console.error('Error parsing stored subsidiary:', e);
      }
    }

    if (!selectedSubsidiary) {
      // D1: the user's own company (or the busiest), not simply the first in the list
      const preferred = defaults?.subsidiary_id ? subsidiaries.find(s => s.id === defaults.subsidiary_id) : undefined;
      setSelectedSubsidiaryState(preferred || subsidiaries[0]);
    }
  }, [subsidiaries, defaults, defaultsLoading]);

  // Update localStorage when selection changes. A null choice is treated as
  // "All Subsidiaries" so older callers that pass null don't silently clear
  // the user's persisted preference.
  const setSelectedSubsidiary = (subsidiary: Subsidiary | null) => {
    const value = subsidiary ?? ALL_SUBSIDIARIES_SENTINEL;
    setSelectedSubsidiaryState(value);
    localStorage.setItem(SUBSIDIARY_STORAGE_KEY, JSON.stringify(value));
  };

  return (
    <SubsidiaryContext.Provider
      value={{
        selectedSubsidiary,
        subsidiaries,
        setSelectedSubsidiary,
        isLoading,
        error: error as Error | null,
      }}
    >
      {children}
    </SubsidiaryContext.Provider>
  );
};

export const useSubsidiary = () => {
  const context = useContext(SubsidiaryContext);
  if (context === undefined) {
    throw new Error('useSubsidiary must be used within a SubsidiaryProvider');
  }
  return context;
};